// ========================================
// DISPUTE SERVICE (SIMPLE MVP)
// ========================================
// src/services/disputes/disputeService.ts

import apiClient from '@/api/apiClient';
import { getCurrentUserId } from '@/utils/authHelper';

export interface Dispute {
    id: number;
    documentId: string;
    disputeNumber: string;
    payment?: {
        id: number;
        documentId: string;
        paymentId: string;
    };
    license?: {
        id: number;
        documentId: string;
        licenseNumber: string;
    };
    filedBy: {
        id: number;
        username: string;
        email: string;
    };
    filedAt: string;
    issueType: string;
    title: string;
    description: string;
    screenshots?: Array<{
        id: number;
        url: string;
        name: string;
    }>;
    status: 'submitted' | 'reviewing' | 'resolved' | 'closed';
    assignedTo?: {
        id: number;
        username: string;
    };
    adminNotes?: string;
    resolvedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateDisputeData {
    paymentId?: string;
    licenseId?: string;
    issueType: 'payment_not_received' | 'license_not_activated' | 'cannot_download' | 'wrong_license' | 'quality_issue' | 'other';
    title: string;
    description: string;
    screenshots?: File[];
}

export interface DisputeFilters {
    status?: string[];
    issueType?: string[];
    dateFrom?: string;
    dateTo?: string;
}

class DisputeService {
    private readonly DISPUTE_PREFIX = 'DSP';

    // ========================================
    // POPULATE & FILTERS
    // ========================================

    private buildPopulateParams(): Record<string, string> {
        return {
            'populate[payment][fields][0]': 'paymentId',
            'populate[license][fields][0]': 'licenseNumber',
            'populate[filedBy][fields][0]': 'username',
            'populate[filedBy][fields][1]': 'email',
            'populate[assignedTo][fields][0]': 'username',
            'populate[screenshots]': 'true'
        };
    }

    private buildFilterParams(filters: DisputeFilters = {}): Record<string, string> {
        const params: Record<string, string> = {};

        if (filters.status?.length) {
            filters.status.forEach((status, i) => {
                params[`filters[status][$in][${i}]`] = status;
            });
        }

        if (filters.issueType?.length) {
            filters.issueType.forEach((type, i) => {
                params[`filters[issueType][$in][${i}]`] = type;
            });
        }

        if (filters.dateFrom) {
            params['filters[filedAt][$gte]'] = filters.dateFrom;
        }

        if (filters.dateTo) {
            params['filters[filedAt][$lte]'] = filters.dateTo;
        }

        return params;
    }

    // ========================================
    // CREATE DISPUTE
    // ========================================

    /**
     * Create new dispute
     */
    async createDispute(data: CreateDisputeData): Promise<Dispute> {
        try {
            console.log('📝 Creating dispute');

            const userId = getCurrentUserId();
            if (!userId) {
                throw new Error('Vui lòng đăng nhập');
            }

            // 1. Upload screenshots if any
            let uploadedFiles: number[] = [];
            if (data.screenshots?.length) {
                uploadedFiles = await this.uploadScreenshots(data.screenshots);
            }

            // 2. Create dispute record
            const disputeData = {
                disputeNumber: this.generateDisputeNumber(),
                filedBy: userId,
                payment: data.paymentId,
                license: data.licenseId,
                issueType: data.issueType,
                title: data.title,
                description: data.description,
                screenshots: uploadedFiles,
                status: 'submitted',
                filedAt: new Date().toISOString()
            };

            const response = await apiClient.post<{ data: Dispute }>(
                '/api/disputes',
                { data: disputeData }
            );

            const dispute = response.data.data;

            console.log('✅ Dispute created:', dispute.disputeNumber);

            // 3. Notify admin via email
            await this.notifyAdminNewDispute(dispute);

            return dispute;
        } catch (error: any) {
            console.error('❌ Failed to create dispute:', error);
            throw new Error(
                error.message || 'Không thể tạo báo cáo'
            );
        }
    }

    // ========================================
    // QUERY DISPUTES
    // ========================================

    /**
     * Get disputes for current user
     */
    async getMyDisputes(
        filters: DisputeFilters = {},
        page: number = 1,
        pageSize: number = 20
    ): Promise<{
        disputes: Dispute[];
        pagination: any;
    }> {
        try {
            const userId = getCurrentUserId();
            if (!userId) {
                throw new Error('Vui lòng đăng nhập');
            }

            const filterParams = this.buildFilterParams(filters);
            const params = {
                ...filterParams,
                'filters[filedBy][id][$eq]': userId.toString(),
                'pagination[page]': page.toString(),
                'pagination[pageSize]': pageSize.toString(),
                'sort': 'filedAt:desc',
                ...this.buildPopulateParams()
            };

            const response = await apiClient.get<{
                data: Dispute[];
                meta: { pagination: any };
            }>('/api/disputes', { params });

            return {
                disputes: response.data.data,
                pagination: response.data.meta.pagination
            };
        } catch (error: any) {
            console.error('Failed to fetch disputes:', error);
            throw new Error('Không thể tải danh sách báo cáo');
        }
    }

    /**
     * Get single dispute
     */
    async getDisputeById(documentId: string): Promise<Dispute> {
        try {
            const params = this.buildPopulateParams();

            const response = await apiClient.get<{ data: Dispute }>(
                `/api/disputes/${documentId}`,
                { params }
            );

            if (!response.data?.data) {
                throw new Error('Dispute not found');
            }

            return response.data.data;
        } catch (error: any) {
            console.error('Failed to fetch dispute:', error);
            throw new Error('Không thể tải thông tin báo cáo');
        }
    }

    /**
     * Get admin queue (for admin/support)
     */
    async getAdminDisputeQueue(
        filters: DisputeFilters = {},
        page: number = 1,
        pageSize: number = 20
    ): Promise<{
        disputes: Dispute[];
        pagination: any;
    }> {
        try {
            // For admin - no user filter
            const filterParams = this.buildFilterParams(filters);
            const params = {
                ...filterParams,
                'pagination[page]': page.toString(),
                'pagination[pageSize]': pageSize.toString(),
                'sort': 'filedAt:desc',
                ...this.buildPopulateParams()
            };

            const response = await apiClient.get<{
                data: Dispute[];
                meta: { pagination: any };
            }>('/api/disputes', { params });

            return {
                disputes: response.data.data,
                pagination: response.data.meta.pagination
            };
        } catch (error: any) {
            console.error('Failed to fetch admin queue:', error);
            throw new Error('Không thể tải hàng đợi xử lý');
        }
    }

    // ========================================
    // ADMIN ACTIONS
    // ========================================

    /**
     * Assign dispute to admin
     */
    async assignDispute(documentId: string, adminId: number): Promise<void> {
        try {
            await apiClient.put(`/api/disputes/${documentId}`, {
                data: {
                    assignedTo: adminId,
                    status: 'reviewing'
                }
            });

            console.log('✅ Dispute assigned');
        } catch (error) {
            console.error('Failed to assign dispute:', error);
            throw new Error('Không thể phân công xử lý');
        }
    }

    /**
     * Update dispute status
     */
    async updateDisputeStatus(
        documentId: string,
        status: string,
        adminNotes?: string
    ): Promise<void> {
        try {
            const updateData: any = { status };

            if (adminNotes) {
                updateData.adminNotes = adminNotes;
            }

            if (status === 'resolved' || status === 'closed') {
                updateData.resolvedAt = new Date().toISOString();
            }

            await apiClient.put(`/api/disputes/${documentId}`, {
                data: updateData
            });

            console.log('✅ Dispute status updated');
        } catch (error) {
            console.error('Failed to update dispute:', error);
            throw new Error('Không thể cập nhật trạng thái');
        }
    }

    // ========================================
    // HELPERS
    // ========================================

    private generateDisputeNumber(): string {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');

        return `${this.DISPUTE_PREFIX}-${year}${month}-${random}`;
    }

    private async uploadScreenshots(files: File[]): Promise<number[]> {
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            const response = await apiClient.post<any[]>(
                '/api/upload',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            return response.data.map((file: any) => file.id);
        } catch (error) {
            console.error('Failed to upload screenshots:', error);
            throw new Error('Không thể upload ảnh');
        }
    }

    private async notifyAdminNewDispute(dispute: Dispute): Promise<void> {
        try {
            // Send email to admin
            await apiClient.post('/api/notifications/admin-dispute', {
                disputeId: dispute.documentId,
                disputeNumber: dispute.disputeNumber,
                title: dispute.title,
                issueType: dispute.issueType
            });
        } catch (error) {
            console.error('Failed to notify admin:', error);
            // Don't throw - notification failure shouldn't break dispute creation
        }
    }
}

export const disputeService = new DisputeService();
